import type { FC, ReactNode } from 'react'
import type { FaqItem, Scene } from '@/types'

import { ArrowUpRight } from 'lucide-react'
import FaqList from '@/components/generals/faq-list'
import SectionLabel from '@/components/generals/section-label'
import SplitReveal from '@/components/motion/split-reveal'
import TransitionLink from '@/components/motion/transition-link'

interface FaqSectionProps {
    index?: string
    label?: string
    title: ReactNode
    intro: string
    items: FaqItem[]
    scene?: Scene
}

// Questions with the heading held in place on the left while the answers scroll.
const FaqSection: FC<FaqSectionProps> = ({
    index,
    label = 'Questions',
    title,
    intro,
    items,
    scene = 'paper'
}): ReactNode => {
    return (
        <section id='faq' data-scene={scene} aria-labelledby='faq-title' className='px-5 py-28 md:px-8 md:py-44'>
            <div className='grid gap-14 lg:grid-cols-12 lg:gap-10'>
                <div className='lg:col-span-5'>
                    <div className='lg:sticky lg:top-28'>
                        <SectionLabel index={index}>
                            {label}
                        </SectionLabel>

                        <SplitReveal as='h2' id='faq-title' className='type-h2 mt-8'>
                            {title}
                        </SplitReveal>

                        <p className='mt-8 max-w-[40ch] text-lg text-quiet'>
                            {intro}
                        </p>

                        <TransitionLink href='/contact' className='group mt-10 inline-flex items-center gap-2 font-medium'>
                            <span className='link-line'>
                                Ask us something else
                            </span>

                            <ArrowUpRight aria-hidden='true' className='size-4' />
                        </TransitionLink>
                    </div>
                </div>

                <div className='lg:col-span-7'>
                    <FaqList items={items} />
                </div>
            </div>
        </section>
    )
}

export default FaqSection
